import React from 'react';

import { createRoot } from 'react-dom/client';

import MasterWidget from '../../../core/components/MasterWidget/MasterWidget';
import { WidgetResult } from '../../../core/hooks/useAnalytics';
import { InitialiserProps } from '../../../core/types/InitialiserProps';
import { WidgetType } from '../../../core/types/WidgetType';
import { createAiredaleFeedsGetter } from '../../../sharedModules/api/airedale/createAiredaleFeedsGetter';
import { getJSON, postForm } from '../../../sharedModules/api/apiUtils';
import { createModelOffersResponseGetter } from '../../../sharedModules/api/searchApi/createModelOffersResponseGetter';
import { createModelSuggestionsGetter } from '../../../sharedModules/api/searchApi/createModelSuggestionsGetter';
import { createSeasonalResponseGetter } from '../../../sharedModules/api/searchApi/createSeasonalResponseGetter';
import { createWidgetResponseGetter } from '../../../sharedModules/api/searchApi/createWidgetResponseGetter';
import { createWidgetResponsePost } from '../../../sharedModules/api/searchApi/createWidgetResponsePost';
import getComponent from '../../../sharedModules/getComponent';
import { hideLinkedElements } from '../../../sharedModules/hideLinkedElements';
import { setDataViewports } from '../../../sharedModules/setDataViewPorts';
import { enumFromStringValue } from '../../../utils/enumFromStringValue';
import { getRenderTypeWithDefault } from '../../../utils/getRenderTypeWithDefault';
import { getMerchantDataFromWidgetPlaceholder } from '../../../wss/modules/getMerchantDataFromWidgetPlaceholder';
import type { ProcessedWidget } from '../hawk/processWidgets/processWidget';

import { IRenderWidgetProps } from './model';

const runInitializer = async ({
  renderWidgetProps,
  widgetType,
}: {
  renderWidgetProps: IRenderWidgetProps;
  widgetType: WidgetType;
}): Promise<InitialiserProps | null> => {
  const LoadableComponent = getComponent(widgetType);
  if (!LoadableComponent) {
    return null;
  }

  const component = (await LoadableComponent.load()) as {
    default?: React.FunctionComponent;
  };
  if (!component.default?.getInitialiserProps) {
    return null;
  }

  const { props, result } = await component.default.getInitialiserProps({
    renderProps: renderWidgetProps,
    attributes: renderWidgetProps.placeholder.dataset,
    getWidgetResponse: createWidgetResponseGetter(getJSON),
    getModelOffersResponse: createModelOffersResponseGetter(getJSON),
    getSeasonalResponse: createSeasonalResponseGetter(getJSON),
    getAiredaleFeeds: createAiredaleFeedsGetter(getJSON),
    getModelSuggestions: createModelSuggestionsGetter(getJSON),
    postForWidgetResponse: createWidgetResponsePost(postForm),
    editorial:
      getRenderTypeWithDefault(
        renderWidgetProps.placeholder.getAttribute('data-render-type') || '',
        'editorial',
      ) === 'editorial',
    defaultParams: {
      articleType: renderWidgetProps.articleType,
      articleId: renderWidgetProps.articleId,
      cacheBust: renderWidgetProps.cacheBust ? new Date().getTime() : null,
      now: renderWidgetProps.now,
      battle: window.VAN?.abTest?.battleId,
    },
    origin: 'widgets-clientside',
    dataLinkMerchant: getMerchantDataFromWidgetPlaceholder(
      Object.fromEntries(
        [...renderWidgetProps.placeholder.attributes].map((attr) => [attr.name, attr.value]),
      ),
    ),
  });

  const { widgetType: newWidgetType } = props;
  if (newWidgetType !== widgetType) {
    return runInitializer({ renderWidgetProps, widgetType: newWidgetType });
  }

  return { props, result };
};
export const renderWidget = async (
  renderWidgetProps: IRenderWidgetProps,
): Promise<ProcessedWidget | null> => {
  const { placeholder } = renderWidgetProps;
  const attributes = placeholder.dataset;

  const widgetType = enumFromStringValue(WidgetType, attributes.widgetType);
  if (!widgetType) {
    return null;
  }

  const initializerResult = await runInitializer({ renderWidgetProps, widgetType });
  if (!initializerResult) {
    return null;
  }
  const { props, result } = initializerResult;

  if (result === WidgetResult.SUCCESS) {
    /**
     * Render the widget and hide the linked elements (e.g. POM or M101 widget).
     * We only want to do this when a widget is actually rendered
     * The linked elements themselves use 'data-hawk-fallback-ids' to allow having
     * multiple widgets links
     * We need to link multiple widgets to avoid an edge case with the stacked product
     * title on womanandhome (HAWK-667)
     */
    hideLinkedElements(props.placeholder);

    setDataViewports(props.placeholder);
  }

  const { widgetType: newWidgetType } = props;
  const NewLoadableComponent = getComponent(newWidgetType);
  if (!NewLoadableComponent) {
    return null;
  }

  placeholder.setAttribute('data-result', result);

  const root = createRoot(placeholder);
  root.render(
    <MasterWidget
      {...props}
      Component={NewLoadableComponent}
      renderFallback={result === WidgetResult.PRESERVED}
      serverSideHtml={placeholder.innerHTML}
      result={result}
    />,
  );

  // Don't track skipped widgets
  if (result === WidgetResult.SKIPPED) {
    return null;
  }

  return {
    type: props.widgetType,
    result,
    props,
  };
};
